import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

const GridExportContext = createContext(null);

/**
 * 페이지 단위 그리드 엑셀 내보내기 레지스트리.
 * 하위 그리드가 register 하고, 포커스된 그리드 / 병합 대상 목록을 관리한다.
 */
export function GridExportProvider({ children }) {
  const handlesRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const [focusedId, setFocusedId] = useState(null);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const register = useCallback((handle) => {
    if (!handle?.id) return () => {};
    handlesRef.current.set(handle.id, {
      order: handle.order ?? 100,
      joinKeys: handle.joinKeys || [],
      ...handle,
    });
    // 첫 등록 그리드를 기본 포커스로
    setFocusedId((prev) => prev ?? handle.id);
    bump();
    return () => {
      handlesRef.current.delete(handle.id);
      setFocusedId((prev) => (prev === handle.id ? null : prev));
      bump();
    };
  }, [bump]);

  const setFocused = useCallback((id) => {
    if (!id) return;
    setFocusedId(id);
  }, []);

  const list = useCallback(() => {
    return Array.from(handlesRef.current.values()).sort(
      (a, b) => (a.order ?? 100) - (b.order ?? 100)
    );
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const get = useCallback((id) => handlesRef.current.get(id), []);

  const getFocused = useCallback(() => {
    if (focusedId && handlesRef.current.has(focusedId)) {
      return handlesRef.current.get(focusedId);
    }
    const all = Array.from(handlesRef.current.values()).sort(
      (a, b) => (a.order ?? 100) - (b.order ?? 100)
    );
    return all[0] || null;
  }, [focusedId, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      register,
      setFocused,
      focusedId,
      list,
      get,
      getFocused,
      version,
    }),
    [register, setFocused, focusedId, list, get, getFocused, version]
  );

  return (
    <GridExportContext.Provider value={value}>
      {children}
    </GridExportContext.Provider>
  );
}

export function useGridExportRegistry() {
  const ctx = useContext(GridExportContext);
  if (!ctx) {
    throw new Error('useGridExportRegistry must be used within GridExportProvider');
  }
  return ctx;
}

/** Provider 밖에서도 안전하게 호출 (없으면 null) */
export function useGridExportRegistryOptional() {
  return useContext(GridExportContext);
}

export default GridExportContext;
